import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Home, Phone, Copy, ArrowLeft, CalendarDays } from "lucide-react";
import Seo from "@/lib/seo";
import api from "@/lib/api";

const TYPES = [
  { v: "visite", l: "Visite technique sur site", icon: Home, d: "Un conducteur de travaux se déplace chez vous. C'est l'étape qui débouche sur un devis définitif." },
  { v: "appel", l: "Appel téléphonique", icon: Phone, d: "Un échange rapide pour préciser votre projet avant la visite." },
];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function labelOf(iso, opts) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", opts);
}

export default function RdvPage() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: type, 2: date, 3: coords, 4: success
  const [project, setProject] = useState(null);
  const [typeRdv, setTypeRdv] = useState("visite");
  const [date, setDate] = useState("");
  const [creneau, setCreneau] = useState("");
  const [form, setForm] = useState({ nom: "", prenom: "", telephone: "", email: "", adresse: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => {
      setProject(data);
      const c = data.contact || {};
      setForm(f => ({ ...f, ...c }));
    }).catch(() => toast.error("Projet introuvable"));
    api.get("/availability?days=120").then(({ data }) => setAvail(data)).catch(() => {});
  }, [projectId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!date || !creneau) { toast.error("Choisissez une date et un créneau."); setStep(2); return; }
    if (!form.nom || !form.telephone || !form.email) { toast.error("Renseignez vos coordonnées."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", { project_id: projectId, type_rdv: typeRdv, date, creneau, ...form });
      setConfirmed(data);
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || "Une erreur est survenue, réessayez.");
      if (err?.response?.status === 409) {
        setCreneau(""); setStep(2);
        api.get("/availability?days=120").then(({ data }) => setAvail(data)).catch(() => {});
      }
    } finally { setSubmitting(false); }
  };

  const copyRef = () => {
    navigator.clipboard?.writeText(project.ref);
    toast.success("Numéro copié");
  };

  const allDays = avail?.days || [];
  const days = allDays.filter(d => !d.closed).slice(0, 12);
  const dayInfo = allDays.find(d => d.date === date);
  const slots = dayInfo?.slots || [];

  // ==============================
  // ÉCRAN DE CONFIRMATION
  // ==============================
  if (step === 4 && confirmed && project) {
    return (
      <div className="pt-[100px] md:pt-[140px] pb-32">
        <div className="wrap max-w-2xl">
          <div className="bg-white border border-black/10 rounded-rama-lg p-6 md:p-12 shadow-[0_30px_80px_-30px_rgba(30,30,30,0.15)] text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-bordeaux-700 text-ivory mb-6">
              <Check size={28} strokeWidth={2} />
            </div>
            <div className="font-mono-caption text-bordeaux-700 mb-3">Rendez-vous confirmé</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite leading-[1.05]">
              Merci{form.prenom ? `, ${form.prenom}` : ""}. <br className="hidden md:block" />
              <span className="italic">On se voit bientôt.</span>
            </h1>
            <p className="mt-5 text-ink-soft leading-relaxed">
              Un conducteur de travaux RAMA DÉCO vous a été assigné.
              Vous recevrez un rappel {typeRdv === "appel" ? "avant l'appel" : "avant la visite"}.
            </p>

            {/* Bloc numéro projet — grand et clair */}
            <div className="mt-8 mx-auto max-w-md p-6 rounded-rama-md bg-ivory border border-dashed border-anthracite/25">
              <div className="font-mono-caption text-ink-soft mb-2">Votre numéro de projet</div>
              <div className="flex items-center justify-center gap-3">
                <div data-testid="confirmed-ref" className="font-display text-3xl md:text-4xl tracking-widest text-bordeaux-700">{project.ref}</div>
                <button onClick={copyRef} className="p-2 rounded hover:bg-anthracite/5 text-ink-soft" aria-label="Copier">
                  <Copy size={18} />
                </button>
              </div>
              <p className="text-xs text-ink-soft mt-4 leading-relaxed">
                Conservez ce numéro. Avec votre email <b>{form.email}</b>, vous pourrez retrouver votre projet à tout moment via <Link to="/mon-projet" className="link-underline text-bordeaux-700 font-semibold">Mon projet</Link>.
              </p>
            </div>

            {/* Récap */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="p-4 bg-ivory rounded-rama-sm text-left">
                <div className="font-mono-caption text-ink-soft mb-1">Type</div>
                <div className="font-semibold text-anthracite">{TYPES.find(t => t.v === typeRdv)?.l}</div>
              </div>
              <div className="p-4 bg-ivory rounded-rama-sm text-left">
                <div className="font-mono-caption text-ink-soft mb-1">Quand</div>
                <div className="font-semibold text-anthracite">{new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {creneau}</div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={`/projet/${project.access_token}`} data-testid="go-to-project" className="btn-primary justify-center flex-1">
                Voir mon espace projet <ArrowRight size={16} />
              </Link>
              <Link to="/" className="btn-ghost justify-center flex-1">Retour à l'accueil</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==============================
  // FLOW DE RÉSERVATION (mobile-first)
  // ==============================
  return (
    <div className="pt-[100px] md:pt-[140px] pb-16">
      <Seo title="Prise de rendez-vous | RAMA DÉCO" description="Réservez votre visite technique gratuite." path="/rdv" noindex />
      <div className="wrap max-w-4xl">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-anthracite mb-6">
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="font-mono-caption text-bordeaux-700 mb-3">Planifier un rendez-vous</div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite leading-[1.05]">
          Choisissez votre <span className="italic text-bordeaux-700">créneau.</span>
        </h1>

        {/* Progression */}
        <div className="mt-8 mb-10 flex items-center gap-2">
          {[1,2,3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full transition ${step >= s ? "bg-bordeaux-700" : "bg-anthracite/15"}`} />
          ))}
        </div>

        <form onSubmit={submit} className="space-y-10">
          {/* STEP 1 - Type */}
          <section data-testid="rdv-step-type" className={step >= 1 ? "" : "hidden"}>
            <div className="font-mono-caption text-ink-soft mb-4">1. Type de rendez-vous</div>
            <div className="grid gap-3 md:grid-cols-2">
              {TYPES.map(t => (
                <button
                  type="button"
                  key={t.v}
                  data-testid={`type-${t.v}`}
                  onClick={() => { setTypeRdv(t.v); if (step < 2) setStep(2); }}
                  className={`text-left p-5 md:p-6 rounded-rama-md border-2 transition ${typeRdv === t.v ? "bg-anthracite text-ivory border-anthracite" : "bg-white border-black/10 hover:border-anthracite/40"}`}
                >
                  <t.icon size={22} className={typeRdv === t.v ? "text-ivory mb-3" : "text-bordeaux-700 mb-3"} strokeWidth={1.6} />
                  <div className="font-display text-lg leading-tight">{t.l}</div>
                  <div className={`text-xs mt-1 ${typeRdv === t.v ? "text-ivory/70" : "text-ink-soft"}`}>{t.d}</div>
                </button>
              ))}
            </div>
          </section>

          {/* STEP 2 - Date + Créneau */}
          <section data-testid="rdv-step-date" className={step >= 2 ? "" : "hidden"}>
            <div className="font-mono-caption text-ink-soft mb-4">2. Jour</div>
            {!avail && <div className="text-sm text-ink-soft">Chargement des disponibilités…</div>}
            <div className="flex gap-2 overflow-x-auto pb-3 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory">
              {days.map(d => {
                const iso = d.date;
                const active = date === iso;
                const full = !d.has_free;
                return (
                  <button
                    type="button"
                    key={iso}
                    disabled={full}
                    data-testid={`day-${iso}`}
                    onClick={() => { setDate(iso); setCreneau(""); }}
                    className={`flex-shrink-0 snap-start w-[76px] md:w-[88px] text-center p-3 md:p-4 rounded-rama-md border-2 transition ${
                      full ? "bg-black/5 border-transparent text-ink-soft/50 cursor-not-allowed line-through"
                      : active ? "bg-bordeaux-700 text-ivory border-bordeaux-700"
                      : "bg-white border-black/10 hover:border-anthracite/40 text-anthracite"}`}
                  >
                    <div className="font-mono-caption text-[0.62rem] opacity-80">{labelOf(iso, { weekday: "short" }).replace(".", "")}</div>
                    <div className="font-display text-2xl md:text-3xl mt-1 leading-none">{new Date(`${iso}T12:00:00`).getDate()}</div>
                    <div className={`text-[0.7rem] mt-1 ${active ? "opacity-80" : "text-ink-soft"}`}>{labelOf(iso, { month: "short" }).replace(".", "")}</div>
                  </button>
                );
              })}
            </div>

            {/* Au-delà des 15 prochains jours : choix libre au calendrier */}
            <div className="mt-5 flex flex-wrap items-end gap-4 p-4 bg-white border border-black/10 rounded-rama-md">
              <label className="text-sm text-ink-soft">
                <span className="inline-flex items-center gap-2 font-mono-caption text-ink-soft">
                  <CalendarDays size={14} className="text-bordeaux-700" /> Une autre date, plus tard
                </span>
                <input type="date" data-testid="rdv-date-libre"
                  min={isoOf(new Date(Date.now() + 15 * 864e5))}
                  max={isoOf(new Date(Date.now() + 240 * 864e5))}
                  value={days.some(d => d.date === date) ? "" : date}
                  onChange={(e) => { setDate(e.target.value); setCreneau(""); }}
                  className="block mt-2 py-2.5 px-3 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
              </label>
              <p className="text-xs text-ink-soft max-w-xs leading-relaxed">
                Choisissez librement votre jour au-delà des deux prochaines semaines. Nous confirmons le créneau par
                téléphone.
              </p>
            </div>

            {date && (
              <>
                <div className="font-mono-caption text-ink-soft mt-8 mb-4">Créneau</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(slots.length ? slots : (avail?.creneaux || []).map(c => ({ creneau: c, available: true }))).map(sl => {
                    const c = sl.creneau;
                    const active = creneau === c;
                    const off = !sl.available;
                    return (
                      <button
                        type="button"
                        key={c}
                        disabled={off}
                        data-testid={`creneau-${c}`}
                        onClick={() => { setCreneau(c); setStep(3); setTimeout(() => document.getElementById("rdv-coords")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                        className={`p-4 rounded-rama-sm border-2 text-sm font-medium transition ${
                          off ? "bg-black/5 border-transparent text-ink-soft/50 cursor-not-allowed"
                          : active ? "bg-anthracite text-ivory border-anthracite"
                          : "bg-white border-black/10 hover:border-anthracite/40"}`}
                      >
                        <span className={off ? "line-through" : ""}>{c}</span>
                        {off && <span className="block text-[0.65rem] mt-1 not-italic">{sl.reason || "Indisponible"}</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* STEP 3 - Coordonnées */}
          <section id="rdv-coords" data-testid="rdv-step-coords" className={step >= 3 ? "" : "hidden"}>
            <div className="font-mono-caption text-ink-soft mb-4">3. Vos coordonnées</div>
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Nom *</label>
                  <input required data-testid="rdv-nom" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="w-full py-3 px-4 bg-white border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
                </div>
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Prénom</label>
                  <input data-testid="rdv-prenom" value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} className="w-full py-3 px-4 bg-white border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Téléphone *</label>
                  <input required type="tel" inputMode="tel" data-testid="rdv-tel" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} className="w-full py-3 px-4 bg-white border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
                </div>
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Email *</label>
                  <input required type="email" inputMode="email" data-testid="rdv-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full py-3 px-4 bg-white border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono-caption text-ink-soft block mb-2">Adresse du chantier {typeRdv === "visite" ? "*" : ""}</label>
                <input required={typeRdv === "visite"} data-testid="rdv-adresse" value={form.adresse} onChange={e => setForm({...form, adresse: e.target.value})} className="w-full py-3 px-4 bg-white border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none text-anthracite" />
              </div>
            </div>

            {/* Récap collant en bas mobile / classique desktop */}
            <div className="mt-8 p-5 md:p-6 bg-anthracite text-ivory rounded-rama-md">
              <div className="font-mono-caption text-ivory/60 mb-2">Récapitulatif</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {project && <span><b>{project.ref}</b></span>}
                <span>·</span>
                <span>{TYPES.find(t => t.v === typeRdv)?.l}</span>
                {date && <><span>·</span><span>{new Date(date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</span></>}
                {creneau && <><span>·</span><span>{creneau}</span></>}
              </div>
              <button type="submit" data-testid="rdv-submit" disabled={submitting} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">
                {submitting ? "Envoi…" : "Confirmer le rendez-vous"} {!submitting && <ArrowRight size={16} />}
              </button>
              <p className="text-[0.7rem] text-ivory/60 mt-3 leading-snug">En confirmant, vous acceptez d'être recontacté(e) par RAMA DÉCO pour votre projet.</p>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}
